import { useState, useEffect, useRef } from "react";
import { ArrowRight, CheckCircle2, MessageSquare, Sparkles, Loader2, Clock, AlertTriangle } from "lucide-react";
import { useEvent } from "../hooks/useEvent";
import { useResponses } from "../hooks/useResponses";
import { submitResponse, getEventByJoinCode, getStreamByCode, SubmissionError } from "../lib/api";
import { isVisibleResponse } from "../lib/responses";
import { isSurveyWindowActive, isSurveyWindowUpcoming } from "../lib/surveyWindow";
import type { Event, Response } from "../lib/types";
import { AnswerDispatcher } from "../components/Participant/AnswerDispatcher";

type OwnModerationState = "pending" | "borderline" | "block" | null;

function ownModerationState(responses: Response[], myIds: Set<string>): OwnModerationState {
  const mine = responses.filter((r) => myIds.has(r.id));
  if (mine.length === 0) return null;
  // Prefer the most recently submitted own row
  const latest = mine.reduce((a, b) =>
    new Date(a.submitted_at).getTime() > new Date(b.submitted_at).getTime() ? a : b,
  );
  if (latest.moderation_overridden) return null;
  if (latest.moderation_status === "block") return "block";
  if (latest.moderation_status === "borderline") return "borderline";
  if (latest.moderation_status === "pending") return "pending";
  return null;
}

function ModerationNotice({ state }: { state: OwnModerationState }) {
  if (state === "pending" || state === "borderline") {
    return (
      <div className="flex items-center gap-2 text-[12px] text-rc-charcoal animate-fade-up">
        <Clock className="w-3.5 h-3.5 flex-shrink-0" />
        <p>Wird kurz geprüft, bevor sie auf der Bühne erscheint.</p>
      </div>
    );
  }
  if (state === "block") {
    return (
      <div className="flex items-start gap-2.5 bg-rc-berry/8 border border-rc-berry/30 rounded-xl px-4 py-3 animate-fade-up">
        <AlertTriangle className="w-4 h-4 text-rc-berry flex-shrink-0 mt-0.5" />
        <p className="text-[13px] text-rc-black leading-[1.5]">
          Antwort empfangen. Deine Antwort wird derzeit geprüft und erscheint möglicherweise nicht auf der Bühne.
        </p>
      </div>
    );
  }
  return null;
}

interface Props {
  joinCode: string;
  streamCode: string | null;
}

type Phase = "loading" | "waiting" | "input" | "submitted" | "closed" | "not_found";

export default function ParticipantView({ joinCode, streamCode }: Props) {
  const [event, setEvent] = useState<Event | null>(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [resolvedStreamId, setResolvedStreamId] = useState<string | null>(null);
  const [streamNotFound, setStreamNotFound] = useState(false);

  useEffect(() => {
    getEventByJoinCode(joinCode).then(async (e) => {
      setEvent(e);
      setEventLoading(false);
      if (e && streamCode) {
        const stream = await getStreamByCode(e.id, streamCode);
        if (stream) {
          setResolvedStreamId(stream.id);
        } else {
          setStreamNotFound(true);
        }
      }
    });
  }, [joinCode, streamCode]);

  if (eventLoading) {
    return (
      <div className="min-h-screen bg-rc-cream flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-rc-berry animate-spin" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-rc-cream text-rc-black flex flex-col items-center justify-center text-center px-6 gap-4 font-sans antialiased">
        <p className="text-[11px] font-semibold tracking-[0.18em] text-rc-charcoal uppercase">READYCAL</p>
        <h1 className="text-2xl font-bold tracking-[-0.02em]">Event nicht gefunden.</h1>
        <p className="text-rc-charcoal text-[14px] leading-[1.55] max-w-sm">
          Der Join-Code{" "}
          <span className="font-mono font-semibold text-rc-berry">{joinCode}</span>{" "}
          passt zu keinem aktiven Event.
        </p>
      </div>
    );
  }

  const now = new Date();
  if (isSurveyWindowActive(event.survey_opens_at, event.survey_closes_at, now)) {
    return (
      <AsyncSurveySession
        event={event}
        streamId={resolvedStreamId}
        streamNotFound={streamNotFound}
      />
    );
  }
  if (isSurveyWindowUpcoming(event.survey_opens_at, now)) {
    return <NotYetOpenState event={event} />;
  }

  if (event.event_mode === "survey") {
    return (
      <SurveyParticipantSession
        event={event}
        streamId={resolvedStreamId}
        streamNotFound={streamNotFound}
      />
    );
  }

  return (
    <ParticipantSession
      event={event}
      streamId={resolvedStreamId}
      streamNotFound={streamNotFound}
    />
  );
}

// ─── Survey participant ────────────────────────────────────────────────────

function ResponsesPausedCard() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 py-10">
      <div className="w-14 h-14 rounded-full bg-rc-stone/20 border border-rc-fog flex items-center justify-center">
        <Clock className="w-6 h-6 text-rc-charcoal" />
      </div>
      <div>
        <p className="text-[18px] font-bold text-rc-black tracking-[-0.01em]">
          Wir kuratieren gerade die Beiträge…
        </p>
        <p className="text-[14px] text-rc-charcoal mt-2 leading-[1.55] max-w-xs">
          Die Antworten erscheinen gleich wieder.
        </p>
      </div>
    </div>
  );
}

function SurveyParticipantSession({
  event,
  streamId,
  streamNotFound,
}: {
  event: Event;
  streamId: string | null;
  streamNotFound: boolean;
}) {
  const { event: liveEvent, questions, loading } = useEvent(event.id);
  const live = liveEvent ?? event;
  const displayedIdx = live.displayed_question_index ?? 0;
  const displayedQuestion = questions[displayedIdx] ?? questions[0] ?? null;
  const prevQuestionId = useRef<string | null>(null);

  const [phase, setPhase] = useState<Phase>("loading");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ripple, setRipple] = useState(false);
  const [myResponseIds, setMyResponseIds] = useState<Set<string>>(new Set());

  const answeredKey = displayedQuestion
    ? `readycal_answered_${event.id}_${displayedQuestion.id}`
    : null;
  const alreadyAnswered = answeredKey ? !!localStorage.getItem(answeredKey) : false;

  useEffect(() => {
    if (loading) return;
    if (!displayedQuestion) {
      setPhase("waiting");
      return;
    }
    if (displayedQuestion.id !== prevQuestionId.current) {
      prevQuestionId.current = displayedQuestion.id;
      const answered = !!localStorage.getItem(`readycal_answered_${event.id}_${displayedQuestion.id}`);
      setText("");
      setError(null);
      setPhase(answered ? "submitted" : "input");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedQuestion, loading]);

  const handleSubmit = async () => {
    if (!displayedQuestion || !text.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const inserted = await submitResponse({ questionId: displayedQuestion.id, text: text.trim(), streamId });
      if (inserted?.id) {
        setMyResponseIds((prev) => new Set(prev).add(inserted.id));
      }
      localStorage.setItem(`readycal_answered_${event.id}_${displayedQuestion.id}`, "1");
      setRipple(true);
      setTimeout(() => {
        setRipple(false);
        setPhase("submitted");
      }, 700);
    } catch (err) {
      if (err instanceof SubmissionError) {
        setError(err.message);
      } else {
        setError("Senden fehlgeschlagen. Bitte erneut versuchen.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmit();
  };

  const { responses } = useResponses(
    phase === "submitted" ? displayedQuestion?.id ?? null : null,
    { limit: 100 },
  );
  const visibleResponses = responses.filter((r) => isVisibleResponse(r)).slice(-20);
  const ownState = ownModerationState(responses, myResponseIds);
  const total = questions.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-rc-cream flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-rc-berry animate-spin" />
      </div>
    );
  }

  return (
    <CardShell joinCode={event.join_code} statusKind="survey">
      {ripple && (
        <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
          <div className="w-24 h-24 rounded-full border-2 border-rc-berry/60 animate-ping" />
          <div
            className="absolute w-16 h-16 rounded-full border-2 border-rc-berry/40 animate-ping"
            style={{ animationDelay: "0.1s" }}
          />
        </div>
      )}

      {streamNotFound && (
        <StreamNotFoundNotice />
      )}

      {total > 1 && (
        <div className="flex gap-1.5 mb-5">
          {questions.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors duration-500 ${
                i === displayedIdx
                  ? "bg-rc-berry"
                  : i < displayedIdx
                    ? "bg-rc-berry/35"
                    : "bg-rc-fog"
              }`}
            />
          ))}
        </div>
      )}

      <main className="flex-1 flex flex-col gap-6">
        {live.responses_paused ? (
          <ResponsesPausedCard />
        ) : (
          <>
            {phase === "waiting" && <WaitingState eventTitle={event.title} isSurvey />}

            {(phase === "input" || phase === "submitted") && displayedQuestion && (
              <>
                <div className="pt-1">
                  {total > 0 && (
                    <p className="text-[11px] font-semibold tracking-[0.18em] text-rc-berry uppercase mb-3">
                      Frage {displayedIdx + 1} / {total}
                    </p>
                  )}
                  <h1 className="text-rc-black font-bold text-[26px] sm:text-[30px] leading-[1.15] tracking-[-0.015em]">
                    {displayedQuestion.text}
                  </h1>
                  {displayedQuestion.hint && (
                    <p className="text-[15px] text-rc-charcoal mt-4 leading-[1.55]">
                      {displayedQuestion.hint}
                    </p>
                  )}
                </div>

                {phase === "submitted" || alreadyAnswered ? (
                  <SubmittedState responses={visibleResponses} ownState={ownState} />
                ) : (
                  <AnswerDispatcher
                    question={displayedQuestion}
                    streamId={streamId}
                    onSubmitted={(row) => {
                      if (row.id) {
                        setMyResponseIds((prev) => new Set(prev).add(row.id));
                      }
                      if (answeredKey) {
                        localStorage.setItem(answeredKey, "1");
                      }
                      setRipple(true);
                      setTimeout(() => {
                        setRipple(false);
                        setPhase("submitted");
                      }, 700);
                    }}
                    onError={(message) => setError(message)}
                    renderOpenText={() => (
                      <>
                        <ResponseInput
                          text={text}
                          setText={setText}
                          onKeyDown={handleKeyDown}
                          placeholder="Tippe deine Antwort hier…"
                        />
                        {error && <ErrorBanner message={error} />}
                        <div className="mt-auto pt-4 flex justify-center">
                          <SubmitButton
                            onClick={handleSubmit}
                            disabled={submitting || !text.trim()}
                            submitting={submitting}
                            label="Antwort senden"
                          />
                        </div>
                      </>
                    )}
                  />
                )}
              </>
            )}
          </>
        )}
      </main>
    </CardShell>
  );
}

// ─── Async pre-survey participant ─────────────────────────────────────────

function NotYetOpenState({ event }: { event: Event }) {
  const opens = event.survey_opens_at ? new Date(event.survey_opens_at) : null;
  const formatted = opens
    ? opens.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })
    : "";
  return (
    <CardShell joinCode={event.join_code} statusKind="survey">
      <main className="flex-1 flex flex-col items-center justify-center text-center gap-5 py-10">
        <div className="w-14 h-14 rounded-full bg-rc-berry/10 border border-rc-berry/25 flex items-center justify-center">
          <Clock className="w-6 h-6 text-rc-berry" />
        </div>
        <div>
          <p className="text-[20px] font-bold text-rc-black tracking-[-0.01em]">
            Die Umfrage startet bald.
          </p>
          <p className="text-[15px] text-rc-charcoal mt-2 leading-[1.55] max-w-xs">
            <strong className="font-semibold text-rc-black">{event.title}</strong>
            {formatted ? ` — ab ${formatted} Uhr kannst du hier antworten.` : ""}
          </p>
        </div>
      </main>
    </CardShell>
  );
}

function AsyncSurveyIntro({ event, onStart }: { event: Event; onStart: () => void }) {
  return (
    <CardShell joinCode={event.join_code} statusKind="survey">
      <main className="flex-1 flex flex-col items-center justify-center text-center gap-6 py-10">
        <div className="w-14 h-14 rounded-full bg-rc-berry/10 border border-rc-berry/25 flex items-center justify-center">
          <MessageSquare className="w-6 h-6 text-rc-berry" />
        </div>
        <div className="flex flex-col gap-3">
          <h1 className="text-rc-black font-bold text-[26px] sm:text-[30px] leading-[1.15] tracking-[-0.015em]">
            {event.title}
          </h1>
          <p className="text-[15px] sm:text-[16px] text-rc-charcoal leading-[1.6] max-w-md whitespace-pre-line">
            {event.description}
          </p>
        </div>
        <SubmitButton onClick={onStart} disabled={false} submitting={false} label="Los geht's" />
      </main>
    </CardShell>
  );
}

function AsyncDoneState({ total }: { total: number }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center gap-5 py-10">
      <div className="w-14 h-14 rounded-full bg-rc-lime/25 border border-rc-lime/40 flex items-center justify-center">
        <CheckCircle2 className="w-7 h-7 text-rc-black" />
      </div>
      <div>
        <p className="text-[20px] font-bold text-rc-black tracking-[-0.01em]">
          Alle {total} Fragen beantwortet.
        </p>
        <p className="text-[15px] text-rc-charcoal mt-2 leading-[1.55] max-w-xs">
          Danke für deine Stimme. Die Ergebnisse fließen in den Workshop ein.
        </p>
      </div>
    </div>
  );
}

function AsyncSurveySession({
  event,
  streamId,
  streamNotFound,
}: {
  event: Event;
  streamId: string | null;
  streamNotFound: boolean;
}) {
  const { questions, loading } = useEvent(event.id);
  const [idx, setIdx] = useState<number | null>(null);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const answeredKeyFor = (questionId: string) =>
    `readycal_answered_${event.id}_${questionId}`;
  const isAnswered = (questionId: string) =>
    !!localStorage.getItem(answeredKeyFor(questionId));

  // Optional intro screen: shown once (per device) before the first question
  // when the facilitator enabled it and a description exists.
  const introKey = `readycal_intro_${event.id}`;
  const [introSeen, setIntroSeen] = useState(() => !!localStorage.getItem(introKey));
  const startSurvey = () => {
    localStorage.setItem(introKey, "1");
    setIntroSeen(true);
  };

  // Start at the first unanswered question (resumes an interrupted run).
  useEffect(() => {
    if (loading || idx !== null || questions.length === 0) return;
    const first = questions.findIndex((q) => !isAnswered(q.id));
    setIdx(first === -1 ? questions.length : first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, questions, idx]);

  const advance = (fromIdx: number) => {
    let next = fromIdx + 1;
    while (next < questions.length && isAnswered(questions[next].id)) next += 1;
    setText("");
    setError(null);
    setIdx(next);
  };

  const question = idx !== null && idx < questions.length ? questions[idx] : null;

  const markAnsweredAndAdvance = () => {
    if (!question || idx === null) return;
    localStorage.setItem(answeredKeyFor(question.id), "1");
    advance(idx);
  };

  const handleOpenTextSubmit = async () => {
    if (!question || !text.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitResponse({ questionId: question.id, text: text.trim(), streamId });
      markAnsweredAndAdvance();
    } catch (err) {
      setError(
        err instanceof SubmissionError ? err.message : "Senden fehlgeschlagen. Bitte erneut versuchen.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleOpenTextSubmit();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-rc-cream flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-rc-berry animate-spin" />
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <CardShell joinCode={event.join_code} statusKind="survey">
        <main className="flex-1 flex flex-col gap-6">
          <WaitingState eventTitle={event.title} isSurvey />
        </main>
      </CardShell>
    );
  }

  const showIntro =
    event.survey_intro_enabled &&
    !!event.description?.trim() &&
    !introSeen &&
    !questions.some((q) => isAnswered(q.id));
  if (showIntro) {
    return <AsyncSurveyIntro event={event} onStart={startSurvey} />;
  }

  if (idx === null) {
    return (
      <div className="min-h-screen bg-rc-cream flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-rc-berry animate-spin" />
      </div>
    );
  }

  return (
    <CardShell joinCode={event.join_code} statusKind="survey">
      {streamNotFound && <StreamNotFoundNotice />}

      {questions.length > 1 && (
        <div className="flex gap-1.5 mb-5">
          {questions.map((q, i) => (
            <div
              key={q.id}
              className={`h-1 flex-1 rounded-full transition-colors duration-500 ${
                isAnswered(q.id) ? "bg-rc-berry/35" : i === idx ? "bg-rc-berry" : "bg-rc-fog"
              }`}
            />
          ))}
        </div>
      )}

      <main className="flex-1 flex flex-col gap-6">
        {!question ? (
          <AsyncDoneState total={questions.length} />
        ) : (
          <>
            <div className="pt-1">
              <p className="text-[11px] font-semibold tracking-[0.18em] text-rc-berry uppercase mb-3">
                Frage {idx + 1} / {questions.length}
              </p>
              <h1 className="text-rc-black font-bold text-[26px] sm:text-[30px] leading-[1.15] tracking-[-0.015em]">
                {question.text}
              </h1>
              {question.hint && (
                <p className="text-[15px] text-rc-charcoal mt-4 leading-[1.55]">{question.hint}</p>
              )}
            </div>

            <AnswerDispatcher
              key={question.id}
              question={question}
              streamId={streamId}
              onSubmitted={() => markAnsweredAndAdvance()}
              onError={(message) => setError(message)}
              renderOpenText={() => (
                <>
                  <ResponseInput
                    text={text}
                    setText={setText}
                    onKeyDown={handleKeyDown}
                    placeholder="Tippe deine Antwort hier…"
                  />
                  {error && <ErrorBanner message={error} />}
                  <div className="mt-auto pt-4 flex justify-center">
                    <SubmitButton
                      onClick={handleOpenTextSubmit}
                      disabled={submitting || !text.trim()}
                      submitting={submitting}
                      label="Antwort senden"
                    />
                  </div>
                </>
              )}
            />
            {error && question.question_type !== "open_text" && <ErrorBanner message={error} />}
          </>
        )}
      </main>
    </CardShell>
  );
}

// ─── Facilitated participant ───────────────────────────────────────────────

function ParticipantSession({
  event,
  streamId,
  streamNotFound,
}: {
  event: Event;
  streamId: string | null;
  streamNotFound: boolean;
}) {
  const { event: liveEvent, questions, activeQuestion, loading } = useEvent(event.id);
  const live = liveEvent ?? event;
  const [text, setText] = useState("");
  const [phase, setPhase] = useState<Phase>("loading");
  const [submitting, setSubmitting] = useState(false);
  const [submitCount, setSubmitCount] = useState(0);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingRetry, setPendingRetry] = useState<{ qid: string; text: string } | null>(null);
  const [myResponseIds, setMyResponseIds] = useState<Set<string>>(new Set());
  const lastQuestionId = useRef<string | null>(null);
  const { responses } = useResponses(activeQuestion?.id ?? null, { limit: 100 });
  const totalQuestions = questions.length;

  useEffect(() => {
    if (loading) return;
    if (!activeQuestion) {
      if (event?.status === "completed") setPhase("closed");
      else setPhase("waiting");
      return;
    }
    if (activeQuestion.id !== lastQuestionId.current) {
      lastQuestionId.current = activeQuestion.id;
      setPhase("input");
      setText("");
      setError(null);
      setSubmitCount(0);
      setJustSubmitted(false);
      setMyResponseIds(new Set());
    }
  }, [activeQuestion, event, loading]);

  useEffect(() => {
    if (!pendingRetry || !navigator.onLine) return;
    const attempt = async () => {
      try {
        await submitResponse({ questionId: pendingRetry.qid, text: pendingRetry.text });
        setPendingRetry(null);
      } catch { /* retry on next online event */ }
    };
    window.addEventListener("online", attempt);
    return () => window.removeEventListener("online", attempt);
  }, [pendingRetry]);

  const handleSubmit = async () => {
    if (!activeQuestion || !text.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    const payload = text.trim();
    try {
      const inserted = await submitResponse({ questionId: activeQuestion.id, text: payload, streamId });
      if (inserted?.id) {
        setMyResponseIds((prev) => new Set(prev).add(inserted.id));
      }
      setText("");
      setSubmitCount((c) => c + 1);
      setJustSubmitted(true);
      setTimeout(() => setJustSubmitted(false), 3000);
    } catch (err) {
      if (err instanceof SubmissionError && (err.code === "rate_limit" || err.code === "paused" || err.code === "closed")) {
        setError(err.message);
      } else if (!navigator.onLine) {
        setPendingRetry({ qid: activeQuestion.id, text: payload });
        setText("");
        setSubmitCount((c) => c + 1);
        setJustSubmitted(true);
        setTimeout(() => setJustSubmitted(false), 3000);
      } else {
        setError(
          err instanceof SubmissionError
            ? err.message
            : "Senden fehlgeschlagen. Bitte erneut versuchen.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmit();
  };

  const visibleResponses = responses.filter((r) => isVisibleResponse(r)).slice(-25);
  const ownState = ownModerationState(responses, myResponseIds);
  const currentIdx = activeQuestion?.order_index ?? 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-rc-cream flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-rc-berry animate-spin" />
      </div>
    );
  }

  return (
    <CardShell joinCode={event.join_code} statusKind="live">
      {totalQuestions > 0 && (
        <div className="flex gap-1.5 mb-5">
          {Array.from({ length: totalQuestions }).map((_, i) => {
            const n = i + 1;
            const active = currentIdx > 0 && n <= currentIdx;
            return (
              <div
                key={n}
                className={`h-1 flex-1 rounded-full transition-colors duration-500 ${
                  active ? "bg-rc-berry" : "bg-rc-fog"
                }`}
              />
            );
          })}
        </div>
      )}

      {streamNotFound && (
        <StreamNotFoundNotice />
      )}

      <main className="flex-1 flex flex-col gap-6">
        {live.responses_paused ? (
          <ResponsesPausedCard />
        ) : (
          <>
            {phase === "waiting" && <WaitingState eventTitle={event.title} isSurvey={false} />}
            {phase === "closed" && <ClosedState />}

            {phase === "input" && activeQuestion && (
              <>
                <div className="pt-1">
                  {totalQuestions > 0 && (
                    <p className="text-[11px] font-semibold tracking-[0.18em] text-rc-berry uppercase mb-3">
                      Frage {activeQuestion.order_index} / {totalQuestions}
                    </p>
                  )}
                  <h1 className="text-rc-black font-bold text-[26px] sm:text-[30px] leading-[1.15] tracking-[-0.015em]">
                    {activeQuestion.text}
                  </h1>
                  {activeQuestion.hint && (
                    <p className="text-[15px] text-rc-charcoal mt-4 leading-[1.55]">
                      {activeQuestion.hint}
                    </p>
                  )}
                </div>

                {justSubmitted && (
                  <div className="flex items-center gap-2.5 bg-rc-lime/20 border border-rc-lime/45 rounded-xl px-4 py-3 animate-fade-up">
                    <CheckCircle2 className="w-4 h-4 text-rc-black flex-shrink-0" />
                    <p className="text-[14px] text-rc-black font-semibold">
                      {pendingRetry ? "Gespeichert. Wird gesendet, sobald wieder online." : "Antwort gesendet."}
                      {submitCount > 1 && (
                        <span className="text-rc-charcoal font-medium"> · {submitCount} insgesamt</span>
                      )}
                    </p>
                  </div>
                )}

                <ModerationNotice state={ownState} />

                <AnswerDispatcher
                  question={activeQuestion}
                  streamId={streamId}
                  onSubmitted={(row) => {
                    if (row.id) {
                      setMyResponseIds((prev) => new Set(prev).add(row.id));
                    }
                    setText("");
                    setSubmitCount((c) => c + 1);
                    setJustSubmitted(true);
                    setTimeout(() => setJustSubmitted(false), 3000);
                  }}
                  onError={(message) => setError(message)}
                  renderOpenText={() => (
                    <>
                      <ResponseInput
                        text={text}
                        setText={setText}
                        onKeyDown={handleKeyDown}
                        placeholder={submitCount > 0 ? "Noch ein Gedanke…" : "Tippe deine Antwort hier…"}
                      />
                      {error && <ErrorBanner message={error} />}

                      <div className="mt-auto pt-4 flex justify-center">
                        <SubmitButton
                          onClick={handleSubmit}
                          disabled={submitting || !text.trim()}
                          submitting={submitting}
                          label={submitCount > 0 ? "Weitere senden" : "Antwort senden"}
                        />
                      </div>
                    </>
                  )}
                />

                {submitCount > 0 && visibleResponses.length > 0 && (
                  <RoomPreview responses={visibleResponses} />
                )}
              </>
            )}
          </>
        )}
      </main>
    </CardShell>
  );
}

// ─── Layout primitives ────────────────────────────────────────────────────

function CardShell({
  children,
  joinCode,
  statusKind,
}: {
  children: React.ReactNode;
  joinCode: string;
  statusKind: "live" | "survey";
}) {
  const dotCls = statusKind === "live" ? "bg-rc-berry" : "bg-rc-lime";
  const statusLabel = statusKind === "live" ? "Live" : "Survey";

  return (
    <div className="min-h-screen bg-rc-cream font-sans antialiased">
      <div className="max-w-xl mx-auto px-4 py-5 sm:py-8">
        <div className="bg-rc-paper rounded-rc-xl shadow-cardLg overflow-hidden min-h-[calc(100vh-4rem)] flex flex-col border border-rc-fog relative">
          <div className="px-6 pt-6">
            <div className="flex items-center justify-between mb-1">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.18em] text-rc-charcoal uppercase">
                <span className="relative inline-flex w-1.5 h-1.5">
                  <span className={`absolute inset-0 rounded-full ${dotCls} opacity-70 animate-shimmer`} />
                  <span className={`relative inline-flex w-1.5 h-1.5 rounded-full ${dotCls}`} />
                </span>
                {statusLabel}
              </span>
              <span className="text-[11px] font-mono font-semibold tracking-[0.18em] text-rc-slate uppercase">
                {joinCode}
              </span>
            </div>
          </div>

          <div className="px-6 pt-4 pb-6 flex-1 flex flex-col">{children}</div>
        </div>

        <div className="flex items-center justify-center gap-2.5 mt-4 text-[10px] font-semibold tracking-[0.18em] text-rc-slate uppercase">
          <span>Ein READYCAL-Produkt</span>
          <span aria-hidden className="text-rc-fog">·</span>
          <a
            href="/datenschutz"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-rc-charcoal transition-colors"
          >
            Datenschutz
          </a>
        </div>
      </div>
    </div>
  );
}

function ResponseInput({
  text,
  setText,
  onKeyDown,
  placeholder,
}: {
  text: string;
  setText: (v: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  placeholder: string;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      <label className="text-[11px] font-semibold tracking-[0.14em] text-rc-charcoal uppercase">
        Deine Antwort
      </label>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        maxLength={600}
        rows={5}
        autoFocus
        className="w-full bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-4 py-3.5 text-rc-black placeholder:text-rc-slate/70 text-[16px] leading-[1.5] resize-none focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors ease-rc-out"
      />
      <div className="flex items-center justify-between text-[11px] text-rc-slate font-medium">
        <span className="tabular-nums">{text.length}/600</span>
        <span className="hidden sm:inline">⌘↵ zum Senden</span>
      </div>
    </div>
  );
}

function SubmitButton({
  onClick,
  disabled,
  submitting,
  label,
}: {
  onClick: () => void;
  disabled: boolean;
  submitting: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="group inline-flex items-center gap-2 bg-rc-berry hover:bg-rc-berry-700 disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper font-semibold rounded-pill pl-6 pr-5 py-3.5 text-[15px] transition-colors ease-rc-out shadow-berry disabled:shadow-none active:scale-[0.97]"
    >
      {submitting ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Sende…
        </>
      ) : (
        <>
          {label}
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </>
      )}
    </button>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <p className="text-[14px] text-rc-danger font-medium bg-rc-danger/8 border border-rc-danger/30 rounded-xl px-3 py-2">
      {message}
    </p>
  );
}

function RoomPreview({
  responses,
  fill = false,
}: {
  responses: { id: string; text: string }[];
  fill?: boolean;
}) {
  return (
    <div className={`border-t border-rc-fog pt-5 mt-2 ${fill ? "flex-1 flex flex-col min-h-0" : ""}`}>
      <div className="flex items-center gap-2 mb-3 flex-shrink-0">
        <Sparkles className="w-3.5 h-3.5 text-rc-slate" />
        <p className="text-[11px] font-semibold tracking-[0.14em] text-rc-charcoal uppercase">
          Andere Stimmen
        </p>
      </div>
      <div
        className={`flex flex-col gap-2 overflow-y-auto -mr-2 pr-2 ${
          fill ? "flex-1 min-h-0" : "max-h-60"
        }`}
      >
        {responses.map((r) => (
          <div
            key={r.id}
            className="bg-rc-cream border border-rc-fog rounded-xl px-4 py-3 text-[14px] text-rc-charcoal leading-[1.55]"
          >
            {r.text}
          </div>
        ))}
      </div>
    </div>
  );
}

function SubmittedState({
  responses,
  ownState,
}: {
  responses: { id: string; text: string }[];
  ownState: OwnModerationState;
}) {
  const subline =
    ownState === "pending" || ownState === "borderline"
      ? "Wird kurz geprüft, bevor sie auf der Bühne erscheint."
      : "Deine Antwort wurde aufgezeichnet.";
  return (
    <div className="flex-1 flex flex-col gap-3 min-h-0">
      <div className="flex items-center gap-3 bg-rc-lime/20 border border-rc-lime/45 rounded-xl px-4 py-3 animate-fade-up flex-shrink-0">
        {ownState === "pending" || ownState === "borderline" ? (
          <Clock className="w-5 h-5 text-rc-black flex-shrink-0" />
        ) : (
          <CheckCircle2 className="w-5 h-5 text-rc-black flex-shrink-0" />
        )}
        <div>
          <p className="text-[14px] text-rc-black font-semibold">Antwort gesendet.</p>
          <p className="text-[12px] text-rc-charcoal">{subline}</p>
        </div>
      </div>

      {ownState === "block" && <ModerationNotice state="block" />}

      {responses.length > 0 && <RoomPreview responses={responses} fill />}
    </div>
  );
}

function WaitingState({ eventTitle, isSurvey }: { eventTitle: string; isSurvey: boolean }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center gap-5 py-10">
      <div className="w-14 h-14 rounded-full bg-rc-berry/10 border border-rc-berry/25 flex items-center justify-center">
        <MessageSquare className="w-6 h-6 text-rc-berry" />
      </div>
      <div>
        <p className="text-[20px] font-bold text-rc-black tracking-[-0.01em]">
          {isSurvey ? "Noch keine Fragen." : "Warten auf die nächste Frage."}
        </p>
        <p className="text-[15px] text-rc-charcoal mt-2 leading-[1.55] max-w-xs">
          <strong className="font-semibold text-rc-black">{eventTitle}</strong>.{" "}
          {isSurvey
            ? "Fragen erscheinen gleich."
            : "Die Moderation öffnet sie gleich."}
        </p>
      </div>
    </div>
  );
}

function ClosedState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center gap-5 py-10">
      <div className="w-14 h-14 rounded-full bg-rc-lime/25 border border-rc-lime/40 flex items-center justify-center">
        <CheckCircle2 className="w-7 h-7 text-rc-black" />
      </div>
      <div>
        <p className="text-[20px] font-bold text-rc-black tracking-[-0.01em]">Session beendet.</p>
        <p className="text-[15px] text-rc-charcoal mt-2">Danke fürs Mitmachen.</p>
      </div>
    </div>
  );
}

function StreamNotFoundNotice() {
  return (
    <div className="flex items-start gap-2.5 bg-rc-fog/60 border border-rc-fog rounded-xl px-4 py-3 mb-2 animate-fade-up">
      <AlertTriangle className="w-4 h-4 text-rc-slate flex-shrink-0 mt-0.5" />
      <p className="text-[13px] text-rc-charcoal leading-[1.5]">
        Stream-Code nicht erkannt. Deine Antwort wird ohne Standortzuordnung gesendet.
      </p>
    </div>
  );
}
